import React, { useEffect, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components';
import { HelpCircle, PlusSquare, Search, X } from 'lucide-react';
import Button from 'components/_UI/Button';
import Ilustration from 'components/_UI/Ilustration';
import Loader from 'components/_UI/Loader';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import CreatePromotionalCode from './CreatePromotionalCode';
import DiscountCodeCard, { statusOf } from './DiscountCodeCard';
import promotionalCodesApi from 'api/requests/promotionalCodes';
import { useEvent } from 'context/events/Event';
import DeleteDiscountCodeModal from './DeleteDiscountCodeModal';
import Banner from 'components/_UI/Banner';
import SalesBar from '../_sales/SalesBar';
import { STATUS, tone, toneForPercent, percentOf, displayPercent, formatInt } from '../_sales/salesTokens';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import useEventAccess from 'hooks/useEventAccess';
import { PERMISSIONS } from 'helpers/panelPermissions';
import { EVENT_PERMISSIONS } from 'helpers/eventCollaboration';

const HELP_STORAGE_KEY = 'understood';

const FILTERS = [
    { id: 'all', label: 'Todos' },
    { id: STATUS.AVAILABLE, label: 'Ativos' },
    { id: STATUS.SOLD_OUT, label: 'Esgotados' },
    { id: STATUS.DISABLED, label: 'Desativados' },
];

const SORTS = [
    { id: 'default', label: 'Padrão' },
    { id: 'uses', label: 'Mais usados' },
    { id: 'code', label: 'A–Z' },
];

/* Busca e filtros só aparecem quando a lista passa a ser difícil de varrer com
   os olhos — com dois ou três cupons eles seriam mais ruído do que ajuda. */
const TOOLBAR_THRESHOLD = 4;

export default function PromotionalCodes() {
    const { toast, toasts } = useToast();
    const { event } = useEvent();
    const { canManage } = useEventAccess();
    const canManageCoupons = canManage(PERMISSIONS.COUPONS_MANAGE, EVENT_PERMISSIONS.EDIT);

    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState();
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [edit, setEdit] = useState(false);
    const [understood, setUnderstood] = useState(false)

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [sort, setSort] = useState('default');

    useEffect(() => {
        getDiscountCodes();
    }, [event?.slug])

    /* Lido só na montagem: reler a cada mudança de `understood` desfazia o clique
       em "Como funciona", que reabre a explicação já dispensada. */
    useEffect(() => {
        setUnderstood(Boolean(localStorage.getItem(HELP_STORAGE_KEY)))
    }, [])

    /* Digitar não pode reordenar a grade a cada tecla: o resultado só se assenta
       quando o produtor para de digitar. */
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 200);
        return () => clearTimeout(timer);
    }, [searchInput]);

    function confirmUnderstood() {
        setUnderstood(true)
        localStorage.setItem(HELP_STORAGE_KEY, true)
    }

    async function getDiscountCodes() {
        setLoading(true);

        try {
            const { data } = await promotionalCodesApi.getByEvent(event?.slug);
            setContent(data);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }

    function onEditClick(item) {
        setEdit(item);
        setModalVisible(true);
    }

    function refreshTickets() {
        setModalVisible(false);
        setDeleteModal(false);
        setEdit(false);

        getDiscountCodes();
    }

    async function onCopyLinkClick(code) {
        const link = `${import.meta.env.VITE_CLIENT_URL}/evento/${event?.slug}?coupon=${code}`;
        copyToClipboard(link, 'Link copiado com sucesso!');
    }

    async function onCopyClick(code) {
        copyToClipboard(code, 'Código copiado com sucesso!');
    }

    async function copyToClipboard(value, message) {
        if (navigator?.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(value)
                toast.success(message, { position: 'top-center' })
                return
            } catch (error) {
            }
        }

        copyToClipboardFallback(value, message);
    }

    function copyToClipboardFallback(value, message) {
        const input = document.createElement('input');
        input.value = value;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        toast.success(message, { position: 'top-center' })
    }

    const codes = useMemo(() => (Array.isArray(content) ? content : []), [content]);

    /* Resumo do topo: quantos cupons ainda valem e quanto do estoque de usos já
       foi resgatado — as duas perguntas que a lista antiga obrigava a somar na
       mão, card a card. */
    const summary = useMemo(() => {
        return codes.reduce((acc, el) => {
            const maxUses = Number(el?.max_uses) || 0;
            const uses = Number(el?.uses) || 0;

            acc.total += 1;
            acc.uses += uses;
            acc.maxUses += maxUses;
            acc.remaining += Math.max(maxUses - uses, 0);

            if (statusOf(el) === STATUS.AVAILABLE) acc.active += 1;

            return acc;
        }, { total: 0, active: 0, uses: 0, maxUses: 0, remaining: 0 });
    }, [codes]);

    const counts = useMemo(() => {
        return codes.reduce((acc, el) => {
            const status = statusOf(el);
            acc[status] = (acc[status] || 0) + 1;
            acc.all += 1;
            return acc;
        }, { all: 0 });
    }, [codes]);

    const visibleCodes = useMemo(() => {
        const filtered = codes.filter(el => {
            const matchesStatus = activeFilter === 'all' || statusOf(el) === activeFilter;
            const matchesSearch = !search || (el?.code || '').toLowerCase().includes(search);

            return matchesStatus && matchesSearch;
        });

        if (sort === 'uses') {
            return [...filtered].sort((a, b) => (Number(b?.uses) || 0) - (Number(a?.uses) || 0));
        }

        if (sort === 'code') {
            return [...filtered].sort((a, b) => (a?.code || '').localeCompare(b?.code || '', 'pt-BR'));
        }

        return filtered;
    }, [codes, activeFilter, search, sort]);

    const usagePercent = percentOf(summary.uses, summary.maxUses);
    const usageTone = tone[toneForPercent(usagePercent)];
    const showToolbar = codes.length >= TOOLBAR_THRESHOLD;
    const isFiltering = activeFilter !== 'all' || Boolean(search);

    function clearFilters() {
        setActiveFilter('all');
        setSearchInput('');
        setSearch('');
    }

    function openCreate() {
        setEdit(false);
        setModalVisible(true);
    }

    return (
        <Container>
            <ToastContainer toasts={toasts} />

            <PageHeader>
                <Column width='auto'>
                    <PageTitle>Códigos promocionais</PageTitle>
                    <PageSubtitle>Cupons de desconto para divulgar e vender mais este evento</PageSubtitle>
                </Column>

                <HeaderActions>
                    {/* A explicação era descartada para sempre no primeiro "Entendi".
                        Agora ela volta por aqui quando a dúvida reaparece. */}
                    {understood && (
                        <GhostButton type='button' onClick={() => setUnderstood(false)}>
                            <HelpCircle size={15} strokeWidth={2.5} aria-hidden='true' />
                            Como funciona
                        </GhostButton>
                    )}

                    {canManageCoupons && (
                        <Button
                            variant='featured'
                            category='squired'
                            marginBottom={0}
                            onClick={openCreate}
                        >
                            <PlusSquare color={colors.neutralLight} size={16} />
                            <ButtonLabel>Criar código</ButtonLabel>
                        </Button>
                    )}
                </HeaderActions>
            </PageHeader>

            {modalVisible &&
                <CreatePromotionalCode
                    visible={modalVisible}
                    isEdit={edit}
                    toast={toast}
                    onClose={() => {
                        setEdit(false);
                        setModalVisible(false);
                    }}
                    onSuccess={refreshTickets}
                />
            }

            {
                deleteModal &&
                <DeleteDiscountCodeModal
                    visible={!!deleteModal}
                    data={deleteModal}
                    toast={toast}
                    onClose={() => setDeleteModal(false)}
                    onSuccess={refreshTickets}
                />
            }

            {!understood && (
                <BannerWrapper>
                    <Banner
                        type='warning'
                        textColor={colors.night}
                        iconColor={colors.night}
                        title='Como funciona o uso de códigos promocionais'
                        text={
                            <>
                                O código promocional é aplicado ao pedido inteiro, e não a cada ingresso ou produto.
                                <br />
                                Exemplo: se a pessoa comprar 3 ingressos e usar um cupom, será descontado 1 cupom apenas,
                                e o desconto será aplicado sobre o valor total do pedido.
                            </>
                        }
                        rightSlot={() => (
                            <ButtonContainer>
                                {/* Aviso é contexto, não alarme: o botão laranja cheio competia
                                    em peso com o "Criar código", que é a ação real da tela. */}
                                <DismissButton type='button' onClick={() => confirmUnderstood()}>
                                    Entendi
                                </DismissButton>
                            </ButtonContainer>
                        )}
                    />
                </BannerWrapper>
            )}

            {loading ? (
                <ContentLoader>
                    <Loader />
                </ContentLoader>
            ) : (
                codes.length ? (
                    <>
                        <SummaryGrid>
                            <SummaryCard $featured>
                                <SummaryLabel>Usos dos cupons</SummaryLabel>
                                <SummaryValue>
                                    {formatInt(summary.uses)}
                                    <SummaryOf>de {formatInt(summary.maxUses)}</SummaryOf>
                                </SummaryValue>
                                <SummaryBar>
                                    <SalesBar
                                        percent={usagePercent}
                                        tone={usageTone}
                                        height={6}
                                        label={`${formatInt(summary.uses)} de ${formatInt(summary.maxUses)} usos resgatados`}
                                    />
                                </SummaryBar>
                                <SummaryHint>{displayPercent(usagePercent)}% resgatado</SummaryHint>
                            </SummaryCard>

                            <SummaryCard>
                                <SummaryLabel>Códigos ativos</SummaryLabel>
                                <SummaryValue>
                                    {formatInt(summary.active)}
                                    <SummaryOf>de {formatInt(summary.total)}</SummaryOf>
                                </SummaryValue>
                                <SummaryHint>prontos para uso agora</SummaryHint>
                            </SummaryCard>

                            <SummaryCard>
                                <SummaryLabel>Usos restantes</SummaryLabel>
                                <SummaryValue>{formatInt(summary.remaining)}</SummaryValue>
                                <SummaryHint>ainda podem ser resgatados</SummaryHint>
                            </SummaryCard>
                        </SummaryGrid>

                        <Section>
                            {showToolbar && (
                                <Toolbar>
                                    <SearchField>
                                        <Search size={15} strokeWidth={2.5} aria-hidden='true' />
                                        <SearchInput
                                            type='search'
                                            value={searchInput}
                                            placeholder='Buscar por código'
                                            aria-label='Buscar código promocional'
                                            onChange={(e) => setSearchInput(e.target.value)}
                                        />
                                        {searchInput && (
                                            <ClearSearch
                                                type='button'
                                                onClick={() => setSearchInput('')}
                                                aria-label='Limpar busca'
                                            >
                                                <X size={14} strokeWidth={2.5} aria-hidden='true' />
                                            </ClearSearch>
                                        )}
                                    </SearchField>

                                    <PillGroup role='group' aria-label='Filtrar por status'>
                                        {FILTERS.map(filter => (
                                            <Pill
                                                key={filter.id}
                                                type='button'
                                                $active={activeFilter === filter.id}
                                                aria-pressed={activeFilter === filter.id}
                                                onClick={() => setActiveFilter(filter.id)}
                                            >
                                                {filter.label}
                                                <PillCount>{counts[filter.id] || 0}</PillCount>
                                            </Pill>
                                        ))}
                                    </PillGroup>

                                    <SortField>
                                        <SortLabel>Ordenar</SortLabel>
                                        <PillGroup role='group' aria-label='Ordenar códigos'>
                                            {SORTS.map(option => (
                                                <Pill
                                                    key={option.id}
                                                    type='button'
                                                    $active={sort === option.id}
                                                    aria-pressed={sort === option.id}
                                                    onClick={() => setSort(option.id)}
                                                >
                                                    {option.label}
                                                </Pill>
                                            ))}
                                        </PillGroup>
                                    </SortField>
                                </Toolbar>
                            )}

                            {visibleCodes.length ? (
                                <DiscountCodesList>
                                    {visibleCodes.map(el => (
                                        <DiscountCodeCard
                                            key={el?.id}
                                            data={el}
                                            onDelete={canManageCoupons ? () => setDeleteModal(el) : undefined}
                                            onEdit={canManageCoupons ? () => onEditClick(el) : undefined}
                                            onCopy={() => onCopyClick(el?.code)}
                                            onCopyLink={() => onCopyLinkClick(el?.code)}
                                        />
                                    ))}
                                </DiscountCodesList>
                            ) : (
                                <NoResults>
                                    <NoResultsTitle>Nenhum código encontrado</NoResultsTitle>
                                    <NoResultsText>
                                        {search
                                            ? <>Nada corresponde a “{searchInput.trim()}” com os filtros atuais.</>
                                            : 'Nenhum código com esse status neste evento.'}
                                    </NoResultsText>
                                    {isFiltering && (
                                        <GhostButton type='button' onClick={clearFilters}>
                                            Limpar filtros
                                        </GhostButton>
                                    )}
                                </NoResults>
                            )}
                        </Section>
                    </>
                ) : (
                    <EmptySection>
                        <Picture>
                            <Ilustration name='promotionalCode' />
                        </Picture>

                        <EmptyTitle>Você ainda não tem códigos promocionais</EmptyTitle>
                        <EmptyText>
                            Crie um cupom de desconto para divulgar em redes sociais, parceiros e influenciadores —
                            e acompanhe por aqui quantas vezes cada um foi usado.
                        </EmptyText>

                        {canManageCoupons && (
                            <EmptyAction>
                                <Button variant='featured' category='squired' onClick={openCreate}>
                                    <PlusSquare color={colors.neutralLight} size={16} />
                                    <ButtonLabel>Criar código promocional</ButtonLabel>
                                </Button>
                            </EmptyAction>
                        )}
                    </EmptySection>
                )
            )}
        </Container>
    )
}

/* ─────────────────────────────  Layout  ───────────────────────────── */

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    animation: ${fadeUp} 0.3s ease both;
`;

const PageHeader = styled.header`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    margin-bottom: 18px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const PageTitle = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const PageSubtitle = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${colors.night};
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;

    & > *:last-child {
        flex: 1;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        & > *:last-child {
            flex: initial;
        }
    }
`;

const ButtonLabel = styled.span`
    margin-left: 6px;
    color: ${colors.neutralLight};
    white-space: nowrap;
`;

const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 38px;
    padding: 0 14px;
    border-radius: 8px;
    border: 1px solid #e2e4ea;
    background: ${colors.neutralLight};
    color: ${colors.night};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &:hover {
        background: #f7efff;
        border-color: #e5d5f8;
        color: #6d14cc;
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

const BannerWrapper = styled.div`
    margin-bottom: 18px;
`;

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: auto;
    }
`;

const DismissButton = styled.button`
    height: 34px;
    padding: 0 18px;
    border-radius: 8px;
    border: 1px solid #d9b877;
    background: rgba(255, 255, 255, 0.75);
    color: #7a5300;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease;

    &:hover {
        background: ${colors.neutralLight};
    }

    &:focus-visible {
        outline: 2px solid #8a5300;
        outline-offset: 2px;
    }
`;

/* ── Resumo ── */

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 16px;

    @media screen and (min-width: ${breakpoints.smd}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const SummaryCard = styled.div`
    padding: 14px 16px;
    border-radius: 14px;
    background: ${({ $featured }) => ($featured ? '#F7EFFF' : colors.neutralLight)};
    border: 1px solid ${({ $featured }) => ($featured ? '#DFC8F8' : 'rgba(16, 24, 40, 0.1)')};
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
`;

const SummaryLabel = styled.p`
    margin: 0;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: ${colors.night};
`;

const SummaryValue = styled.p`
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin: 6px 0 0;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.6px;
    line-height: 1.1;
    color: ${colors.nightDark};
`;

const SummaryOf = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const SummaryBar = styled.div`
    margin-top: 10px;
`;

const SummaryHint = styled.p`
    margin: 6px 0 0;
    font-size: 12px;
    color: ${colors.night};
`;

/* ── Lista ── */

const Section = styled.section`
    padding: 18px;
    border-radius: 16px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(16, 24, 40, 0.1);
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
`;

const Toolbar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
    }
`;

const SearchField = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 10px;
    border-radius: 10px;
    border: 1px solid #e2e4ea;
    background: ${colors.neutralLight};
    color: ${colors.nightLight};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    flex: 1;
    min-width: 200px;

    &:focus-within {
        border-color: ${panelAccent(colors.secondaryPurple)};
        box-shadow: 0 0 0 3px ${panelAccentAlpha(colors.secondaryPurple, '1f')};
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        max-width: 260px;
    }
`;

const SearchInput = styled.input`
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: ${colors.nightDark};

    &::placeholder {
        color: ${colors.nightLight};
    }

    &::-webkit-search-cancel-button {
        display: none;
    }
`;

const ClearSearch = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: ${colors.night};
    cursor: pointer;

    &:hover {
        background: ${colors.neutralDark};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 1px;
    }
`;

const SortField = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const SortLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.night};
    white-space: nowrap;
`;

const PillGroup = styled.div`
    display: inline-flex;
    max-width: 100%;
    padding: 3px;
    border-radius: 10px;
    background: ${colors.neutralBackground};
    flex-shrink: 0;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const Pill = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;

    background: ${({ $active }) => ($active ? colors.neutralLight : 'transparent')};
    color: ${({ $active }) => ($active ? colors.nightDark : colors.night)};
    box-shadow: ${({ $active }) => ($active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none')};

    &:hover {
        color: ${colors.nightDark};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

const PillCount = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.night};
`;

/* Grade fluida: a largura mínima do card manda no número de colunas, sem os
   `nth-of-type` que a lista antiga usava para emendar as margens. */
const DiscountCodesList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(272px, 1fr));
    gap: 14px;
    width: 100%;
`;

const NoResults = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 36px 16px;
    text-align: center;
`;

const NoResultsTitle = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const NoResultsText = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${colors.night};
`;

/* ── Estados ── */

const ContentLoader = styled(Column)`
    height: 320px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const EmptySection = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 20px 32px;
    border-radius: 16px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(16, 24, 40, 0.1);
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
`;

const Picture = styled.picture`
    width: auto;
    height: 260px;
`;

const EmptyTitle = styled.p`
    margin: -18px 0 0;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    text-align: center;
`;

const EmptyText = styled.p`
    margin: 8px 0 0;
    max-width: 440px;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.night};
    text-align: center;
`;

const EmptyAction = styled.div`
    width: 260px;
    margin-top: 18px;
`;
